import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { IconButton, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import groupBy from "lodash/groupBy";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import { useImages } from "../../media/useImages";
import DictionaryAccordion from "./DictionaryAccordion";
import EditTagDialog from "./EditTagDialog";
import { useMediaTags } from "./useMediaTags";

interface AddTagFooterProps {
  readonly onAdd: (name: string) => Promise<void>;
}

const AddTagFooter: FC<AddTagFooterProps> = ({ onAdd }) => {
  const [name, setName] = useState("");
  return (
    <TableBody>
      <TableRow>
        <TableCell>
          <TextField fullWidth label="Nazwa" value={name} onChange={(e) => setName(e.target.value)} />
        </TableCell>
        <TableCell />
        <TableCell>
          <AsyncLoadingButton
            disabled={!name}
            onClick={async () => {
              await onAdd(name);
              setName("");
            }}
          >
            Dodaj
          </AsyncLoadingButton>
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

interface TagRowProps {
  readonly tag: { id: string; name: string; isPopular: boolean };
  readonly imagesForTag: { imageId: string }[];
  readonly onTagChange: (name: string, isPopular: boolean) => Promise<void>;
  readonly onDelete: () => Promise<void>;
}

const TagRow: FC<TagRowProps> = ({ tag, imagesForTag, onTagChange, onDelete }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <TableRow data-testid="media-tag-row">
      <TableCell>{tag.name}</TableCell>
      <TableCell>
        {imagesForTag.length > 0 ? (
          <Link underline="none" href={`/media?query=${tag.name}`}>
            {imagesForTag.length}
          </Link>
        ) : (
          imagesForTag.length
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={2}>
          <IconButton size="small" aria-label="Edytuj nazwę taga" onClick={() => setDialogOpen(true)}>
            <EditIcon />
          </IconButton>
          <IconButton disabled={imagesForTag.length > 0} onClick={onDelete} aria-label="Usuń tag ze słownika">
            <DeleteIcon />
          </IconButton>
        </Stack>
        <EditTagDialog
          open={dialogOpen}
          tagName={tag.name}
          isTagPopular={tag.isPopular}
          onChange={onTagChange}
          onClose={() => setDialogOpen(false)}
        />
      </TableCell>
    </TableRow>
  );
};

const MediaTagsDictionary = () => {
  const { tags, isLoading: areTagsLoading, addTag, updateTag, deleteTag } = useMediaTags();
  const { images, isLoading: areImagesLoading } = useImages();
  const isLoading = areTagsLoading || areImagesLoading;
  const tagsWithImages = images.flatMap((i) =>
    i.tags.map((t) => ({
      tagName: t,
      imageId: i.id,
    })),
  );
  const imagesForTag = groupBy(tagsWithImages, ({ tagName }) => tagName);
  return (
    <DictionaryAccordion title="Tagi zdjęć" isLoading={isLoading}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nazwa</TableCell>
            <TableCell>Liczba wystąpień</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <AddTagFooter
          onAdd={async (n) => {
            await addTag(n, false);
          }}
        />
        <TableBody>
          {tags.map((t) => (
            <TagRow
              key={t.id}
              tag={t}
              imagesForTag={imagesForTag[t.name] ?? []}
              onTagChange={async (name, isPopular) => {
                await updateTag(t.id, name, isPopular);
              }}
              onDelete={() => deleteTag(t.id)}
            />
          ))}
        </TableBody>
      </Table>
    </DictionaryAccordion>
  );
};

export default MediaTagsDictionary;
